import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { successToastify, errorToastify } from "../utils/toastify.util.js";
import { getApiErrorMessage } from "../utils/upload.util.js";
import {
  createDiscountData,
  fetchDiscountList,
  getAdminDiscountList,
  updateDiscountData,
  updateDiscountStatus,
} from "../apis/discount.api.js";
import { fetchItemGroupListAPI } from "../apis/item.api.js";

const initialState = {
  isSpin: false,
  isLoad: false,
  listData: null,
  isUpdate: false,
  flag: null,

  // ✅ give default shape so Formik always has keys
  discountFormData: {
    id: "",
    discount_code: "",
    discount_name: "",
    discount_type: "0",
    amount: "",
    status: "1",
    // discount used-for choice is removed from the form; default to Users
    isAdmin: "0",
    max_users: "",
    validity_days: "",
    // multi-select, one array per pack type
    item_pack_ids: [],
    hunt_pack_ids: [],
    quest_pack_ids: [],
    // date range
    start_date: "",
    end_date: "",
  },

  type: [
    { label: "Flat", value: "0" },
    { label: "Percentage", value: "1" },
  ],

  discountListAdmin: null,

  itemsList: [],
  isLoadingItems: false,
};

// ================= THUNKS =================

export const fetchDiscountListData = createAsyncThunk(
  "discount/fetchDiscountList",
  async (_, thunkAPI) => {
    try {
      const response = await fetchDiscountList();
      return response;
    } catch (err) {
      const { data } = err.response;
      return data;
    }
  }
);

export const fetchAdminDiscountList = createAsyncThunk(
  "discount/getDiscountListForAdmin",
  async (_, thunkAPI) => {
    try {
      const response = await getAdminDiscountList();
      return response;
    } catch (err) {
      const { data } = err.response;
      return data;
    }
  }
);

export const fetchItemsForDiscount = createAsyncThunk(
  "discount/fetchItemsForDiscount",
  async (payload, thunkAPI) => {
    try {
      const response = await fetchItemGroupListAPI(payload);
      return response;
    } catch (err) {
      const message = getApiErrorMessage(err, "Failed to fetch challenge packs");
      return thunkAPI.rejectWithValue({
        success: false,
        message,
        ...(err?.response?.data || {}),
      });
    }
  }
);

export const createDiscount = createAsyncThunk(
  "discount/createDiscount",
  async (payload, { dispatch }) => {
    try {
      const data = await createDiscountData(payload);

      if (data.success) {
        successToastify(
          data?.message || data?.mesasge || "Discount created successfully"
        );

        // ✅ close modal / refresh list
        dispatch(setUpdateSuccess(true));
        dispatch(fetchDiscountListData());

        // ✅ clear form ONLY after success
        dispatch(resetDiscountForm());
      } else {
        errorToastify(
          data?.message || data?.mesasge || "Failed to create discount"
        );
      }
    } catch (err) {
      const data = err?.response?.data;
      errorToastify(
        data?.message || data?.mesasge || "Failed to create discount"
      );
      return data;
    }
  }
);

export const updateDiscount = createAsyncThunk(
  "discount/updateDiscount",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const data = await updateDiscountData(payload);

      if (data.success) {
        successToastify(
          data?.message || data?.mesasge || "Discount updated successfully"
        );

        dispatch(setUpdateSuccess(true));
        dispatch(fetchDiscountListData());
        dispatch(resetDiscountForm());
        return data;
      }

      const message =
        data?.message || data?.mesasge || "Failed to update discount";
      return rejectWithValue({ message, ...data });
    } catch (err) {
      const message = getApiErrorMessage(err, "Failed to update discount");
      return rejectWithValue({
        message,
        ...(err?.response?.data || {}),
      });
    }
  }
);

export const modifyDiscountStatus = createAsyncThunk(
  "discount/updateDiscountStatus",
  async (payload, { dispatch }) => {
    try {
      const data = await updateDiscountStatus(payload);

      if (data.success) {
        successToastify(
          data?.message || data?.mesasge || "Discount updated successfully"
        );
        dispatch(fetchDiscountListData());
      } else {
        errorToastify(
          data?.message || data?.mesasge || "Failed to update discount"
        );
      }
    } catch (err) {
      const data = err?.response?.data;
      errorToastify(
        data?.message || data?.mesasge || "Failed to update discount"
      );
      return data;
    }
  }
);

// ================= SLICE =================

const discountSlice = createSlice({
  name: "discount",
  initialState,
  reducers: {
    /** ✅ use this on edit click */
    setDiscountFormData: (state, { payload }) => {
      const d = payload || {};

      const isValidId = (v) =>
        v !== undefined &&
        v !== null &&
        v !== "" &&
        String(v).toLowerCase() !== "undefined" &&
        String(v).toLowerCase() !== "null";

      // packItems: [{ pack_item_id, pack_type }] as returned via discount.itemgroups
      const packItems = Array.isArray(d.items)
        ? d.items
        : Array.isArray(d.itemgroups)
          ? d.itemgroups
          : [];

      const idsForPackType = (packType) =>
        packItems
          .filter((it) => String(it?.pack_type ?? "0") === packType)
          .map((it) => it?.pack_item_id ?? it?.id)
          .filter(isValidId)
          .map((v) => String(v));

      const start_date = d.start_date ?? d.startDate ?? "";
      const end_date = d.end_date ?? d.endDate ?? "";

      state.discountFormData = {
        ...state.discountFormData,
        ...d,
        item_pack_ids: idsForPackType("0"),
        hunt_pack_ids: idsForPackType("1"),
        quest_pack_ids: idsForPackType("2"),
        start_date,
        end_date,
      };
    },

    setFlag: (state, { payload }) => {
      state.flag = payload;
    },

    /** ✅ ONLY flag change — DO NOT CLEAR FORM HERE */
    setUpdateSuccess: (state, { payload }) => {
      state.isUpdate = payload;
    },

    /** ✅ call this when you want to clear form (after success or modal close) */
    resetDiscountForm: (state) => {
      state.discountFormData = { ...initialState.discountFormData };
    },
  },

  extraReducers: (builder) => {
    builder
      // list
      .addCase(fetchDiscountListData.pending, (state) => {
        state.isLoad = true;
      })
      .addCase(fetchDiscountListData.fulfilled, (state, action) => {
        state.isLoad = false;
        if (action.payload.success) {
          // ✅ don't force clear edit form here
          state.isUpdate = false;
          const result = action.payload.result;
          state.listData = Array.isArray(result) ? result : [];
        } else {
          errorToastify(action.payload?.message || action.payload?.mesasge);
          state.listData = [];
        }
      })
      .addCase(fetchDiscountListData.rejected, (state) => {
        state.isLoad = false;
        errorToastify("Request is rejected..");
      })

      // admin list
      .addCase(fetchAdminDiscountList.pending, (state) => {
        state.isLoad = true;
      })
      .addCase(fetchAdminDiscountList.fulfilled, (state, action) => {
        state.isLoad = false;
        if (action.payload.success) {
          state.discountListAdmin = action.payload.result;
        } else {
          errorToastify(action.payload?.message);
        }
      })
      .addCase(fetchAdminDiscountList.rejected, (state) => {
        state.isLoad = false;
        errorToastify("Request is rejected..");
      })

      // create/update/spin
      .addCase(createDiscount.pending, (state) => {
        state.isSpin = true;
      })
      .addCase(createDiscount.fulfilled, (state) => {
        state.isSpin = false;
      })
      .addCase(updateDiscount.pending, (state) => {
        state.isSpin = true;
      })
      .addCase(updateDiscount.fulfilled, (state) => {
        state.isSpin = false;
      })
      .addCase(modifyDiscountStatus.pending, (state) => {
        state.isSpin = true;
      })
      .addCase(modifyDiscountStatus.fulfilled, (state) => {
        state.isSpin = false;
      })

      // items
      .addCase(fetchItemsForDiscount.pending, (state) => {
        state.isLoadingItems = true;
      })
      .addCase(fetchItemsForDiscount.fulfilled, (state, action) => {
        state.isLoadingItems = false;

        if (action.payload?.success && action.payload?.result) {
          const list = Array.isArray(action.payload.result.data)
            ? action.payload.result.data
            : action.payload.result;

          state.itemsList = list || [];
        } else {
          errorToastify(action.payload?.message || "Failed to fetch challenges");
        }
      })
      .addCase(fetchItemsForDiscount.rejected, (state, action) => {
        state.isLoadingItems = false;
        errorToastify(action.payload?.message || "Failed to fetch challenges");
      });
  },
});

export default discountSlice.reducer;

export const {
  setDiscountFormData,
  setFlag,
  setUpdateSuccess,
  resetDiscountForm,
} = discountSlice.actions;
